// ── 매입 표 줄 입력 (2026-11-03 사용자 "노션처럼 표에서 바로") ──────────────────────
// 새 줄(＋)과 기존 줄 고치기에 같이 쓴다. 붙여넣기 창처럼 테두리 없는 한 줄로 보이고 **누른 칸만** 파란 테두리.
//   Enter 또는 이 줄 밖으로 나가면 onCommit(저장은 표가 판단), Esc 면 onCancel. 맨 오른쪽 🗑 = onTrash(새 줄은 취소).
//   새 줄은 맨 위 빈 줄(＋)이 같은 높이 그대로 이 입력 줄이 된다(5차 — 4차의 펴짐·접힘은 지웠다).
// 금액 칸은 type="text" + inputMode="numeric"(화살표 없음, 음수 가능 — 서버가 콤마를 다듬는다).
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { COLS, KIND_OPTIONS, fmtMoney } from './purchaseRowForm';
import TrashButton from './TrashButton';

export default function PurchaseRowEditor({
  draft, setDraft, focusKey, onCommit, onCancel, onTrash, cellBase, bg, error, busy, lead, rowHeight,
}) {
  const trRef = useRef(null);
  const [focused, setFocused] = useState(null);
  // rowHeight = 바꿔 넣을 보통 줄의 실제 높이(41.3 처럼 소수점). 칸 상자 높이 = 그 높이 − 밑줄 몫(그려 보고 잰다)
  //   → 입력 줄과 보통 줄 높이가 소수점까지 같아서, 누르거나 저장해도 아래 줄이 0.5픽셀도 안 움직인다.
  const [H, setH] = useState(40);
  useLayoutEffect(() => {
    if (!rowHeight || !trRef.current) return;
    const boxNow = trRef.current.cells[0]?.firstChild?.getBoundingClientRect().height ?? 0;
    const extra = trRef.current.getBoundingClientRect().height - boxNow;
    setH(Math.max(40, rowHeight - extra));
  // 처음 그릴 때 한 번만 재면 된다(밑줄 몫은 줄 내용과 상관없다)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 칸 안 상자 — 이 높이가 줄 높이를 정한다(표 줄 자체에 높이를 주면 소수점까지 못 맞춰서)
  const box = (child) => <div style={{ height: H, overflow: 'hidden' }}>{child}</div>;

  useEffect(() => {
    const el = trRef.current?.querySelector(`[data-col="${focusKey || COLS[0].key}"]`);
    el?.focus();
  // 처음 열릴 때 한 번만 — 고르는 칸으로 커서를 옮긴다
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));
  const kindOptions = draft.kind && !KIND_OPTIONS.includes(draft.kind) ? [draft.kind, ...KIND_OPTIONS] : KIND_OPTIONS;

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onCommit(); } // 비고 칸은 Shift+Enter = 줄바꿈
    else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  }
  // 줄 밖으로 커서가 나가면 저장(다른 칸으로 옮기는 건 그대로)
  function onBlur(e) {
    setFocused(null);
    if (trRef.current && !trRef.current.contains(e.relatedTarget)) onCommit();
  }

  const input = (c) => {
    const common = {
      'data-col': c.key, value: draft[c.key] ?? '', onChange: set(c.key), disabled: busy,
      onFocus: () => setFocused(c.key),
      style: { ...inputStyle, textAlign: c.align || 'left', boxShadow: focused === c.key ? focusRing : 'none' },
    };
    if (c.type === 'kind') {
      return (
        <select {...common}>
          <option value="">(없음)</option>
          {kindOptions.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      );
    }
    if (c.type === 'date') return <input type="date" {...common} />;
    if (c.type === 'money') {
      return <input type="text" inputMode="numeric" {...common} onBlur={() => setDraft((d) => ({ ...d, [c.key]: fmtMoney(d[c.key]) }))} />;
    }
    // 비고·비고2(여러 줄로 보이던 칸)는 고칠 때도 그 줄 높이만큼 여러 줄 — 줄이 낮아져 아래가 움직이지 않게
    if (c.wrap) {
      return <textarea {...common} rows={1} style={{ ...common.style, height: H, padding: '10px 12px', resize: 'none', lineHeight: 1.5 }} />;
    }
    return <input type="text" {...common} />;
  };

  return (
    <>
      <tr ref={trRef} onKeyDown={onKeyDown} onBlur={onBlur} style={{ background: bg || 'transparent' }}>
        <td style={{ ...cellBase, textAlign: 'center', padding: 0, verticalAlign: 'top' }}>{box(lead)}</td>
        {COLS.map((c) => <td key={c.key} style={{ ...cellBase, padding: 0, verticalAlign: 'top' }}>{box(input(c))}</td>)}
        <td style={{ ...cellBase, textAlign: 'center', padding: 0, verticalAlign: 'top' }}>
          {box(<div style={{ paddingTop: 4 }}><TrashButton onClick={onTrash} keepFocus /></div>)}
        </td>
      </tr>
      {error && (
        <tr>
          <td colSpan={COLS.length + 2} style={hintCell}>
            <span style={{ color: '#dc2626', fontWeight: 600 }}>{error}</span>
            {/* 커서가 줄 밖이어도(확인 창 뒤 등) 누를 수 있는 취소 — mousedown 을 막아 저장이 먼저 돌지 않게 */}
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onCancel} style={cancelBtn}>취소</button>
          </td>
        </tr>
      )}
    </>
  );
}

// 표의 보통 칸(stdTd 10px 12px·13px, 높이 40)과 글자 자리·높이가 같게 — 테두리 없이 칸을 꽉 채운다
const inputStyle = {
  display: 'block', width: '100%', minWidth: 0, boxSizing: 'border-box', height: 40, padding: '0 12px', fontSize: 13, // 보통 줄 높이(40)와 같게 — 눌러도 아래 줄이 안 움직이게
  border: 'none', borderRadius: 0, background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', outline: 'none',
};
const focusRing = 'inset 0 0 0 2px rgba(59,130,246,0.55)';
const cancelBtn = {
  marginLeft: 10, padding: '2px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--border)',
  background: 'var(--bg-card)', color: 'var(--text-sub)', fontSize: 12, fontWeight: 600,
};
const hintCell = { padding: '4px 12px 8px', fontSize: 12, color: 'var(--text-muted)', borderBottom: '1px solid var(--bg-subtle)' };
